/**
 * Every step that happened to a contact, for the Company Record: how and
 * when it was created, emails, replies, calls, notes, flows, outreach jobs,
 * tags, bounces and opt-outs.
 *
 * Built from fields already on the contact (via buildLeadStatus), the
 * optional sent/received mail list from useContactMailHistory and the
 * contact's activityLog entries. Nothing here writes or fetches.
 */
import { buildLeadStatus, mailbox, toEpoch } from './leadStatusSnapshot';

const SAME_MOMENT_MS = 3 * 60 * 1000;

const IMPORT_LIST_SOURCES = {
  csv_upload: 'CSV / Excel import',
  manual: 'Added manually',
  api: 'API import',
  api_auto: 'API import (auto mode)',
  api_smart: 'Smart API import',
};

const INTERNAL_LIST_NAMES = new Set(['manual_entry', '__api_agent__']);

const REPLY_SUBTYPES = {
  interested: 'Interested', price_objection: 'Price objection', no_need: 'No need',
  later: 'Later', wrong_person: 'Wrong person', hostile: 'Hostile',
  spam_accusation: 'Spam report', neutral: 'Neutral',
};

const OUTREACH_STATUS = {
  completed: 'Outreach completed',
  failed: 'Outreach failed',
  skipped: 'Removed from outreach',
  replied: 'Outreach stopped: replied',
};

const LOG_STYLE = {
  outreach_enrolled: { kind: 'outreach', icon: 'fa-robot' },
  edited: { kind: 'edit', icon: 'fa-pen' },
  status: { kind: 'status', icon: 'fa-flag' },
  email_check: { kind: 'check', icon: 'fa-envelope-circle-check' },
  dm_emails: { kind: 'check', icon: 'fa-at' },
};

const clean = (v, max = 160) => {
  const s = String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

/**
 * When and through which channel the contact entered the account.
 * `listSource` is `import_names/{import_name}.source` when the caller has it.
 */
export function creationInfo(contact, listSource = null) {
  const c = contact || {};
  const at = toEpoch(c._transferredAt) && c._transferredFrom
    ? toEpoch(c._transferredAt)
    : toEpoch(c.imported_at) || toEpoch(c.timestamp) || toEpoch(c.created_at)
      || toEpoch(c.lead_added_at) || toEpoch(c.lead_since);
  const listName = c.import_name && !INTERNAL_LIST_NAMES.has(c.import_name) ? clean(c.import_name, 80) : '';

  let channel;
  let icon = 'fa-file-import';
  if (c._transferredFrom) {
    channel = 'Copied from another workspace';
    icon = 'fa-right-left';
  } else if (c.import_source === 'outreach_api_trigger') {
    channel = 'API · agent trigger';
    icon = 'fa-plug';
  } else if (c.source === 'smart_inbox') {
    channel = 'From an inbox reply';
    icon = 'fa-inbox';
  } else if (c.import_mode === 'smart_reviewed' || c.import_mode === 'smart_reviewed_bulk') {
    channel = 'Smart API import · approved in review';
    icon = 'fa-plug';
  } else if (c.import_mode === 'smart') {
    channel = 'Smart API import';
    icon = 'fa-plug';
  } else if (c.import_name === 'manual_entry' || listSource === 'manual') {
    channel = 'Added manually';
    icon = 'fa-user-plus';
  } else if (listSource && IMPORT_LIST_SOURCES[listSource]) {
    channel = IMPORT_LIST_SOURCES[listSource];
    icon = listSource === 'csv_upload' ? 'fa-file-csv' : 'fa-plug';
  } else if (c.import_source === 'api' || c.apiSourceKeyId) {
    channel = 'API import';
    icon = 'fa-plug';
  } else if (!c.import_name && c.created_at) {
    channel = 'Added in Leads';
    icon = 'fa-user-plus';
  } else if (c.import_name) {
    channel = 'Import';
  } else {
    channel = 'Added';
  }
  return { at, channel, icon, listName };
}

function subjectsClash(a, b) {
  const left = String(a.subject || '').trim().toLowerCase();
  const right = String(b.subject || '').trim().toLowerCase();
  return !!(left && right && left !== right);
}

function pushUnique(list, item) {
  const clash = list.find((other) => other.kind === item.kind && other.at && item.at
    && Math.abs(other.at - item.at) < SAME_MOMENT_MS);
  if (clash) {
    if (!clash.detail && item.detail) clash.detail = item.detail;
    if (!clash.subject && item.subject) clash.subject = item.subject;
    // The contact record and the sent-mail log describe one send. Keep the
    // stored copy so the row can open that exact email. A different subject
    // in the same minute is a different send — don't point the row at it.
    if (!subjectsClash(clash, item)) {
      if (!clash.mailDocId && item.mailDocId) clash.mailDocId = item.mailDocId;
      if (!clash.html && item.html) clash.html = item.html;
      if (!clash.text && item.text) clash.text = item.text;
      if (!clash.from && item.from) clash.from = item.from;
      if (!clash.address && item.address) clash.address = item.address;
      if ((!clash.cc || !clash.cc.length) && item.cc && item.cc.length) clash.cc = item.cc;
    }
    return;
  }
  list.push(item);
}

const GENERIC_EMAIL_TITLES = new Set(['Email sent', 'Follow-up sent']);

function presentStep(item) {
  if (item.kind === 'outreach') {
    return {
      ...item,
      title: OUTREACH_STATUS[String(item.title).replace(/^Outreach /, '')] || item.title,
      subject: '',
    };
  }
  if (item.kind !== 'email') return { ...item, subject: item.subject || '' };
  const raw = String(item.title || '').trim();
  const generic = !raw || GENERIC_EMAIL_TITLES.has(raw) || /emails reached this status$/.test(raw);
  if (generic) {
    const title = raw && !GENERIC_EMAIL_TITLES.has(raw) ? `Email sent: ${raw}` : (raw || 'Email sent');
    return { ...item, title, subject: '' };
  }
  return { ...item, title: `Email sent: ${raw}`, subject: raw };
}

/**
 * Firestore id of the outgoing mail this activity row describes.
 * Prefers the id already attached while the row was built, then a subject
 * match against the sent-mail log, then the only send in the same minute.
 */
export function sentMailDocId(item, mails = []) {
  if (!item || item.kind !== 'email') return '';
  if (item.mailDocId) return item.mailDocId;
  const subject = String(item.subject || '').trim().toLowerCase();
  const outs = mails.filter((m) => m && m.dir === 'out' && m.docId);
  if (subject) {
    const same = outs.filter((m) => String(m.subject || '').trim().toLowerCase() === subject);
    if (same.length === 1) return same[0].docId;
    if (same.length > 1) {
      const ranked = item.at
        ? [...same].sort((a, b) => Math.abs((a.at || 0) - item.at) - Math.abs((b.at || 0) - item.at))
        : same;
      return ranked[0].docId;
    }
  }
  if (item.at) {
    const near = outs.filter((m) => m.at && Math.abs(m.at - item.at) < SAME_MOMENT_MS);
    if (near.length === 1) return near[0].docId;
  }
  return '';
}

/**
 * @param {object} contact
 * @param {{ mails?: Array, logEntries?: Array, listSource?: string|null, now?: number }} [options]
 *   logEntries: docs from the contact's activityLog subcollection
 * @returns {Array<{id, at, kind, icon, title, detail, subject, mailDocId}>} newest first, undated last
 */
export function buildContactActivity(contact, options = {}) {
  const c = contact || {};
  const lead = buildLeadStatus(c, { now: options.now });
  const items = [];

  const created = creationInfo(c, options.listSource);
  items.push({
    id: 'created',
    at: created.at,
    kind: 'created',
    icon: created.icon,
    title: `Created · ${created.channel}`,
    detail: created.listName ? `List: ${created.listName}` : '',
  });

  const replyLog = Array.isArray(c.replyHistory) ? c.replyHistory : [];
  const timeline = lead.timeline.filter((item) => item.kind !== 'added'
    && !(item.kind === 'reply' && replyLog.length > 0));
  timeline.forEach((item) => {
    pushUnique(items, presentStep(item));
  });
  lead.logged.forEach((item) => {
    if (item.kind === 'reply' && replyLog.length > 0) return;
    items.push({ ...presentStep(item), at: null });
  });

  const subtype = REPLY_SUBTYPES[c.lead_reply_subtype] || '';
  replyLog
    .map((r) => ({ at: toEpoch(r?.receivedAt || r?.timestamp), r }))
    .filter((x) => x.at)
    .sort((a, b) => b.at - a.at)
    .forEach((x, idx) => pushUnique(items, {
      id: `reply-${x.at}`,
      at: x.at,
      kind: 'reply',
      icon: 'fa-comment',
      title: 'Reply received',
      detail: [idx === 0 ? subtype : '', x.r?.inReplyTo?.templateName ? `to "${clean(x.r.inReplyTo.templateName, 60)}"` : '']
        .filter(Boolean).join(' · '),
    }));

  (options.mails || []).forEach((m) => {
    if (!m || !m.at) return;
    const out = m.dir === 'out';
    const subject = m.subject && m.subject !== '(no subject)' ? String(m.subject) : '';
    pushUnique(items, {
      id: `mail-${m.id}`,
      at: m.at,
      kind: out ? 'email' : 'reply',
      icon: out ? 'fa-envelope' : 'fa-comment',
      title: out ? `Email sent: ${clean(subject || m.subject, 90)}` : `Reply received: ${clean(m.subject, 90)}`,
      detail: [m.kind ? String(m.kind).replace(/_/g, ' ') : '', m.address].filter(Boolean).join(' · '),
      subject: out ? subject : '',
      mailDocId: out ? (m.docId || '') : '',
      html: out ? (m.html || '') : '',
      text: out ? (m.text || '') : '',
      from: out ? mailbox(m.from) : '',
      address: out ? (m.address || '') : '',
      cc: out ? (m.cc || []) : [],
    });
  });

  const leadAddedAt = toEpoch(c.lead_added_at) || toEpoch(c.lead_since);
  if (leadAddedAt && (!created.at || Math.abs(leadAddedAt - created.at) > SAME_MOMENT_MS)) {
    pushUnique(items, { id: 'lead-added', at: leadAddedAt, kind: 'status', icon: 'fa-star', title: 'Moved to Leads', detail: '' });
  }

  const preparedAt = c.has_prepared_email ? toEpoch(c.prepared_email_at) : null;
  if (preparedAt) {
    pushUnique(items, { id: 'prepared', at: preparedAt, kind: 'prepared', icon: 'fa-pen-to-square', title: 'Email prepared, waiting to send', detail: '' });
  }

  const bounce = c.emailBounceCheck && typeof c.emailBounceCheck === 'object' ? c.emailBounceCheck : null;
  if (bounce && toEpoch(bounce.at)) {
    pushUnique(items, {
      id: 'bounce',
      at: toEpoch(bounce.at),
      kind: 'bounce',
      icon: 'fa-triangle-exclamation',
      title: bounce.reroutedTo ? `Email bounced, switched to ${clean(bounce.reroutedTo, 60)}` : 'Email bounced',
      detail: [bounce.originalEmail, bounce.reason].filter(Boolean).map((v) => clean(v, 80)).join(' · '),
    });
  }

  const optOutAt = toEpoch(c.globalOptOutAt);
  if (optOutAt) pushUnique(items, { id: 'optout', at: optOutAt, kind: 'block', icon: 'fa-hand', title: 'Opted out', detail: '' });
  const blockedAt = toEpoch(c.blacklisted_at) || toEpoch(c.autoBlockedAt);
  if (blockedAt) pushUnique(items, { id: 'blocked', at: blockedAt, kind: 'block', icon: 'fa-ban', title: 'Blocked', detail: '' });

  const unblockedAt = toEpoch(c.globalOptOutRemovedAt);
  if (unblockedAt) pushUnique(items, { id: 'unblocked', at: unblockedAt, kind: 'status', icon: 'fa-unlock', title: 'Removed from blacklist', detail: '' });
  const trashedAt = toEpoch(c.removedAt);
  if (trashedAt) pushUnique(items, { id: 'trashed', at: trashedAt, kind: 'block', icon: 'fa-trash', title: 'Removed from Leads (moved to trash)', detail: '' });
  const restoredAt = toEpoch(c.restoredAt);
  if (restoredAt) pushUnique(items, { id: 'restored', at: restoredAt, kind: 'status', icon: 'fa-rotate-left', title: 'Restored from trash', detail: '' });

  const mergedAt = toEpoch(c.last_merged_at);
  if (mergedAt) {
    pushUnique(items, { id: 'merged', at: mergedAt, kind: 'edit', icon: 'fa-code-merge', title: 'Updated by import (duplicate merged)', detail: '' });
  }

  Object.entries(c.tags && typeof c.tags === 'object' ? c.tags : {}).forEach(([id, tag]) => {
    if (!tag || typeof tag !== 'object') return;
    const at = toEpoch(tag.addedAt || tag.appliedAt);
    if (!at) return;
    items.push({
      id: `tag-${id}`,
      at,
      kind: 'tag',
      icon: 'fa-tag',
      title: `Tag added: ${clean(tag.name || tag.tag || id, 60)}`,
      detail: tag.source === 'outreach_job' ? 'by an outreach job' : '',
    });
  });

  const cleanedAt = toEpoch(c.cleanedAt);
  if (cleanedAt) {
    pushUnique(items, { id: 'cleaned', at: cleanedAt, kind: 'edit', icon: 'fa-broom', title: 'Cleaned by AI Cleaner', detail: '' });
  }

  (options.logEntries || []).forEach((entry) => {
    const at = toEpoch(entry && entry.at);
    const style = entry && LOG_STYLE[entry.type];
    if (!at || !style) return;
    const failed = entry.type === 'email_check' && /failed|invalid/i.test(`${entry.title} ${entry.detail}`);
    items.push({
      id: `log-${entry.id || at}`,
      at,
      kind: failed ? 'bounce' : style.kind,
      icon: failed ? 'fa-triangle-exclamation' : style.icon,
      title: clean(entry.title, 160),
      detail: clean(entry.detail, 300),
    });
  });

  const editedAt = toEpoch(c.updated_at);
  if (editedAt && !items.some((item) => item.at && Math.abs(item.at - editedAt) < SAME_MOMENT_MS)) {
    items.push({ id: 'edited', at: editedAt, kind: 'edit', icon: 'fa-pen', title: 'Contact edited', detail: '' });
  }

  const last = c.lead_last_email;
  const lastHtml = last && typeof last === 'object' ? String(last.content || last.body || '') : '';
  if (lastHtml.trim()) {
    const lastSubject = String(last.subject || last.subjectLineText || '').trim().toLowerCase();
    const lastAt = toEpoch(last.timestamp || last.sentAt || last.date);
    const match = items.find((item) => item.kind === 'email' && !item.html && (
      (lastSubject && String(item.subject || '').trim().toLowerCase() === lastSubject)
      || (lastAt && item.at && Math.abs(item.at - lastAt) < SAME_MOMENT_MS
        && !subjectsClash(item, { subject: last.subject || last.subjectLineText || '' }))
    ));
    if (match) match.html = lastHtml;
  }

  return items.sort((a, b) => {
    if (!a.at && !b.at) return 0;
    if (!a.at) return 1;
    if (!b.at) return -1;
    return b.at - a.at;
  });
}
